import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  ArrowDown,
  CircleNotch as Loader2,
  CloudArrowUp,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  fetchMyDocuments,
  downloadSolicitorDocument,
} from '@/lib/solicitorPortalApi';
import { getApiErrorMessage } from '@/lib/api';
import { SolicitorUploadModal } from '@/components/solicitor/SolicitorUploadModal';

/**
 * Documents view — the primary workspace for a solicitor.
 * Grouped by matter, with the uploader's role called out so the
 * solicitor can spot drafts arriving from counsel.
 */
export default function DocumentsPage() {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['portal', 'documents'],
    queryFn: () => fetchMyDocuments(),
  });

  const handleDownload = async (id: string) => {
    setDownloading(id);
    try {
      // Verified, authenticated download — server re-hashes the bytes before sending.
      await downloadSolicitorDocument(id);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not download this document.'));
    } finally {
      setDownloading(null);
    }
  };

  const byMatter = (data ?? []).reduce<Record<string, { caseNumber: string; caseName: string; docs: typeof data }>>(
    (acc, d) => {
      const k = d.case.id;
      if (!acc[k]) acc[k] = { caseNumber: d.case.caseNumber, caseName: d.case.caseName, docs: [] };
      acc[k].docs!.push(d);
      return acc;
    },
    {},
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Documents</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Briefs you've sent, and opinions, skeletons, and drafts from counsel.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="gap-1.5">
          <CloudArrowUp size={15} weight="fill" />
          Upload document
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading documents…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-5 text-sm text-red-800 dark:text-red-300">
          {error instanceof Error ? error.message : 'Could not load documents.'}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
          <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <div className="mb-1 text-sm font-medium text-foreground">No documents yet</div>
          <p className="text-xs text-muted-foreground">
            Upload a brief or wait for counsel to share something — it will appear here.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="space-y-6">
          {Object.entries(byMatter).map(([matterId, group]) => (
            <div key={matterId} className="overflow-hidden rounded-lg border border-border bg-card">
              <div className="border-b border-border px-5 py-3">
                <div className="text-sm font-semibold text-foreground">{group.caseName}</div>
                <div className="text-xs text-muted-foreground">{group.caseNumber}</div>
              </div>
              <ul className="divide-y divide-border">
                {group.docs!.map((d) => {
                  const fromCounsel = d.uploadedBy.memberType !== 'solicitor';
                  const isDownloading = downloading === d.id;
                  return (
                    <li
                      key={d.id}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-muted/30"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground">
                            {d.name}
                          </span>
                          {fromCounsel && (
                            <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">
                              From counsel
                            </span>
                          )}
                          {d.version > 1 && (
                            <span className="text-[10px] font-medium uppercase text-muted-foreground">
                              v{d.version}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {d.category.replace(/_/g, ' ')}
                          {' · '}
                          {d.uploadedBy.firstName} {d.uploadedBy.lastName}
                          {' · '}
                          {new Date(d.createdAt).toLocaleDateString('en-GB')}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(d.id)}
                        disabled={isDownloading}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isDownloading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <ArrowDown className="h-3.5 w-3.5" />
                        )}
                        Download
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <SolicitorUploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            qc.invalidateQueries({ queryKey: ['portal', 'documents'] });
          }}
        />
      )}
    </div>
  );
}
